import { NS } from '@/common/namespaces';

export type CommandAttributeValue = string | number | boolean | null | CommandAttributes[];

export type CommandAttributes = { [key: string]: CommandAttributeValue };

const getAttributeNamespace = (attributeName: string): string | null => {
    if (attributeName === 'xmlns' || attributeName.startsWith('xmlns:')) return NS.XMLNS;
    if (attributeName.startsWith('xml:')) return NS.XML;
    if (attributeName.startsWith('xlink:')) return NS.XLINK;
    if (attributeName.startsWith('zaa:')) return NS.ZAA;
    return null;
};

const createNode = (data: CommandAttributes, document: Document): Node | null => {
    const elementType = data['#type'];
    if (typeof elementType !== 'string') {
        const text = data['#text'];
        return typeof text === 'string' ? document.createTextNode(text) : null;
    }

    const element = document.createElementNS(NS.SVG, elementType);
    for (const [name, value] of Object.entries(data)) {
        if (name.startsWith('#') || value === null || Array.isArray(value)) {
            continue;
        }

        const stringValue = value.toString();
        const namespace = getAttributeNamespace(name);
        if (namespace) {
            element.setAttributeNS(namespace, name, stringValue);
        } else {
            element.setAttribute(name, stringValue);
        }
    }

    const children = data['#children'];
    if (Array.isArray(children)) {
        for (const childData of children) {
            const child = createNode(childData, document);
            if (child) {
                element.appendChild(child);
            }
        }
    } else if (typeof data['#text'] === 'string') {
        element.textContent = data['#text'];
    }

    return element;
};

const serializeNode = (node: Node): CommandAttributes | null => {
    if (node.nodeType === Node.TEXT_NODE) {
        return { '#text': node.nodeValue ?? '' };
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const element = node as Element;
    const data: CommandAttributes = { '#type': element.tagName };
    for (const attribute of Array.from(element.attributes)) {
        data[attribute.name] = attribute.value;
    }

    data['#children'] = Array.from(element.childNodes)
        .map(serializeNode)
        .filter((child): child is CommandAttributes => child !== null);

    return data;
};

export const serializeChildren = (element: Element): CommandAttributes[] => Array.from(element.childNodes)
    .map(serializeNode)
    .filter((child): child is CommandAttributes => child !== null);

export const replaceChildren = (element: Element, children: CommandAttributeValue): void => {
    if (!Array.isArray(children)) {
        return;
    }

    const childNodes = children
        .map(child => createNode(child, element.ownerDocument))
        .filter((child): child is Node => child !== null);

    element.replaceChildren(...childNodes);
};
